import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Chip, Divider, IconButton,
  Alert, Snackbar, useMediaQuery, useTheme, alpha, Fade
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Gavel as GavelIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { keyframes } from '@emotion/react';
import TermsContent from './TermsContent';
import { hasAcceptedTerms, acceptTerms, getTermsVersion } from '../utils/termsStorage';
import logo from "../assets/icons/logo-blog.png"; // Replace with your actual logo path

const slideUp = keyframes`
  from { transform: translateY(100%); }
  to   { transform: translateY(0); }
`;

const slideDown = keyframes`
  from { transform: translateY(0); }
  to   { transform: translateY(100%); }
`;

const pulse = keyframes`
  0%   { box-shadow: 0 0 0 0   rgba(25, 118, 210, 0.4); }
  70%  { box-shadow: 0 0 0 10px rgba(25, 118, 210, 0); }
  100% { box-shadow: 0 0 0 0   rgba(25, 118, 210, 0); }
`;

const TermsAndConditions = () => {
  const [open, setOpen]       = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [snackbarOpen, setSnackbarOpen]   = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [visible, setVisible] = useState(false);   // controls mount
  const [closing, setClosing] = useState(false);   // triggers exit animation
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (!hasAcceptedTerms()) {
      setTimeout(() => { setVisible(true); setOpen(true); }, 10000);
    }
  }, []);

  const closeWithAnimation = (afterFn) => {
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      setVisible(false);
      setOpen(false);
      afterFn();
    }, 400);
  };

  const handleAccept = () =>
    closeWithAnimation(() => {
      acceptTerms();
      setSnackbarMessage('✓ Thank you for accepting the Terms and Conditions!');
      setSnackbarOpen(true);
    });

  const handleDecline = () =>
    closeWithAnimation(() => {
      setSnackbarMessage('⚠️ You must accept the Terms and Conditions to continue using the website.');
      setSnackbarOpen(true);
    });

  const handleCloseSnackbar = () => setSnackbarOpen(false);
  const toggleExpand = () => setExpanded(!expanded);

  return (
    <>
      {visible && (
        <Dialog
          open={open}
          fullScreen={fullScreen}
          maxWidth={false}
          fullWidth
          scroll="paper"
          PaperProps={{
            sx: {
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              margin: 0,
              width: '100% !important',
              maxWidth: '100% !important',
              borderRadius: { xs: 0, md: '12px 12px 0 0' },
              maxHeight: expanded ? '80vh' : '20vh',
              minHeight: expanded ? '80vh' : '20hvh',
              transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              overflow: 'hidden',
              animation: closing
                ? `${slideDown} 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards`
                : `${slideUp}   0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards`,
              boxShadow: theme.shadows[24],
              borderTop: `3px solid ${theme.palette.primary.main}`,
            }
          }}
        >
          {/* Header */}
          <DialogTitle
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 1.5,
              px: 3,
              cursor: 'pointer',
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              }
            }}
            onClick={toggleExpand}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ animation: `${pulse} 2s infinite`, display: 'flex', alignItems: 'center' }}>
                <img src={logo} alt="Logo" style={{ width: '40px', height: '40px' }} />
              </Box>
              <Box>
                <Typography variant="h6" component="span" fontWeight="bold" sx={{ fontSize: '1rem' }}>
                  Terms and Conditions
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', opacity: 0.8, fontSize: '0.65rem' }}>
                  Version {getTermsVersion()} | Last Updated: January 1, 2026
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ display: { xs: 'none', sm: 'block' } }}>
                {expanded ? 'Click to collapse' : 'Click to read terms'}
              </Typography>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              <IconButton
                aria-label="close"
                onClick={(e) => { e.stopPropagation(); handleDecline(); }}
                sx={{
                  color: 'white',
                  '&:hover': { backgroundColor: alpha(theme.palette.common.white, 0.1) }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>

          {/* Content */}
          {expanded && (
            <DialogContent dividers sx={{ p: 0, overflow: 'auto', position: 'relative', flex: 1 }}>
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                  <Chip
                    label="🔒 Legal Agreement"
                    size="small"
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      fontWeight: 500
                    }}
                  />
                  <Chip
                    label="Must accept to continue"
                    size="small"
                    color="warning"
                    sx={{ borderRadius: 2, fontWeight: 500, fontSize: '0.7rem' }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 2, fontStyle: 'italic' }}>
                  Please read these terms and conditions carefully before using our website.
                  By accessing our services, you agree to be bound by these terms.
                </Typography>
                <Divider sx={{ my: 2 }} />
                <TermsContent />
              </Box>
            </DialogContent>
          )}

          {/* Footer */}
          <DialogActions sx={{
            p: 2,
            gap: 2,
            justifyContent: 'space-between',
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
            backgroundColor: alpha(theme.palette.background.default, 0.95),
          }}>
            <Button
              variant="outlined"
              color="error"
              onClick={handleDecline}
              startIcon={<CancelIcon />}
              sx={{
                minWidth: 120, borderRadius: 2, textTransform: 'none', fontWeight: 600,
                '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.04) }
              }}
            >
              Decline
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAccept}
              startIcon={<CheckCircleIcon />}
              autoFocus
              sx={{
                minWidth: 160, borderRadius: 2, textTransform: 'none', fontWeight: 600,
                boxShadow: theme.shadows[2],
                '&:hover': { transform: 'translateY(-1px)', boxShadow: theme.shadows[4] },
                transition: 'all 0.2s ease'
              }}
            >
              Accept &amp; Continue
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={Fade}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarMessage.includes('Thank you') ? 'success' : 'warning'}
          sx={{ width: '100%', borderRadius: 2, boxShadow: theme.shadows[3] }}
        >
          <Typography variant="body2" fontWeight={500}>{snackbarMessage}</Typography>
        </Alert>
      </Snackbar>
    </>
  );
};

export default TermsAndConditions;